/**
 * @fileoverview Ultra-reactive mobile frontend component for stadium fans playing "Esquivador" (Surfer - Dynamic Id: 51).
 * Features 100dvh full-screen gestural swipe controls (100% touchpad, no virtual buttons),
 * real-time SignalR multi-tenant synchronization, Screen Wake Lock management,
 * native haptic vibration, zero-dependency Web Audio feedback, and graceful handling of the 'kicked_out' pattern.
 * 
 * @module components/Dashboard/Sections/SurferFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ConnectWebSocketForSurferFan,
    DisconnectWebSocketForSurferFan
} from '../../../utilities/WS/SurferFanWS';
import SignalRService from '../../../utilities/WS/SignalRService';
import SurferBL, {
    ResolveMediaUrl,
    SelectPlayerSide,
    SendPlayerAction,
    GetConfig,
    GetRealtimeState
} from '../../../utilities/BL/Surfer';

/**
 * Storage keys for persistent session management.
 */
const STORAGE_KEY_SIDE = 'surfer_player_side';
const STORAGE_KEY_NAME = 'surfer_player_name';

/**
 * Default theme accent colors.
 */
const DEFAULT_COLORS = {
    player1: '#00FFCC',
    player2: '#FF007F',
    bgDark: '#0a0b12',
    accentGold: '#FFD700',
    neonGreen: '#00FF66'
};

/**
 * Default fallback avatars in SVG format for runners when no theme assets are loaded.
 */
const DEFAULT_AVATAR_P1 = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="%23051a24" stroke="%2300FFCC" stroke-width="4"/><path d="M50 24c-8 0-14 6-14 14 0 7 5 13 12 14v6l-14 18h8l10-14 10 14h8L66 58v-6c7-1 12-7 12-14 0-8-6-14-14-14z" fill="%2300FFCC"/><circle cx="50" cy="38" r="6" fill="%23051a24"/></svg>`;
const DEFAULT_AVATAR_P2 = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="46" fill="%23240515" stroke="%23FF007F" stroke-width="4"/><path d="M50 24c-8 0-14 6-14 14 0 7 5 13 12 14v6l-14 18h8l10-14 10 14h8L66 58v-6c7-1 12-7 12-14 0-8-6-14-14-14z" fill="%23FF007F"/><circle cx="50" cy="38" r="6" fill="%23240515"/></svg>`;

/**
 * Web Audio Synthesizer for low-latency zero-dependency game sound effects.
 */
class SurferAudioSynthesizer {
    constructor() {
        this.ctx = null;
        this.isUnlocked = false;
    }

    unlock() {
        if (this.isUnlocked) return;
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (AudioContext) {
                this.ctx = new AudioContext();
                if (this.ctx.state === 'suspended') {
                    this.ctx.resume();
                }
                this.isUnlocked = true;
            }
        } catch (e) {
            console.warn('[SurferAudio] AudioContext unlock error:', e);
        }
    }

    playSwipe() {
        if (!this.ctx) this.unlock();
        if (!this.ctx || this.ctx.state !== 'running') return;
        try {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            osc.type = 'sine';
            const now = this.ctx.currentTime;
            osc.frequency.setValueAtTime(280, now);
            osc.frequency.exponentialRampToValueAtTime(620, now + 0.08);
            gain.gain.setValueAtTime(0.2, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(now);
            osc.stop(now + 0.09);
        } catch (e) {}
    }

    playHit() {
        if (!this.ctx) this.unlock();
        if (!this.ctx || this.ctx.state !== 'running') return;
        try {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            osc.type = 'sawtooth';
            const now = this.ctx.currentTime;
            osc.frequency.setValueAtTime(160, now);
            osc.frequency.exponentialRampToValueAtTime(45, now + 0.2);
            gain.gain.setValueAtTime(0.4, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(now);
            osc.stop(now + 0.21);
        } catch (e) {}
    }

    playCountdownTick() {
        if (!this.ctx) this.unlock();
        if (!this.ctx || this.ctx.state !== 'running') return;
        try {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            osc.type = 'triangle';
            const now = this.ctx.currentTime;
            osc.frequency.setValueAtTime(520, now);
            gain.gain.setValueAtTime(0.25, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(now);
            osc.stop(now + 0.13);
        } catch (e) {}
    }

    playCountdownGo() {
        if (!this.ctx) this.unlock();
        if (!this.ctx || this.ctx.state !== 'running') return;
        try {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            osc.type = 'triangle';
            const now = this.ctx.currentTime;
            osc.frequency.setValueAtTime(880, now);
            gain.gain.setValueAtTime(0.35, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(now);
            osc.stop(now + 0.36);
        } catch (e) {}
    }

    playCoin() {
        if (!this.ctx) this.unlock();
        if (!this.ctx || this.ctx.state !== 'running') return;
        try {
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            osc.type = 'sine';
            const now = this.ctx.currentTime;
            osc.frequency.setValueAtTime(987.77, now); // B5
            osc.frequency.setValueAtTime(1318.51, now + 0.06); // E6
            gain.gain.setValueAtTime(0.25, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.18);
            osc.connect(gain);
            gain.connect(this.ctx.destination);
            osc.start(now);
            osc.stop(now + 0.19);
        } catch (e) {}
    }
}

const audioSynth = new SurferAudioSynthesizer();

/**
 * SurferFan Component
 * Mobile touch controller for Esquivador (Surfer).
 * 
 * @function SurferFan
 * @param {Object} props - Component properties.
 * @param {string} [props.roomId] - Room GUID.
 * @param {string} [props.playerSide] - Initial player side ('player1' | 'player2').
 * @param {string} [props.side] - Alias for playerSide.
 * @param {Object} [props.matchState] - Initial match state if passed by router.
 * @param {Object} [props.config] - Technical configuration if passed by router.
 * @param {string} [props.winner] - Winner if known.
 * @returns {React.ReactElement}
 */
export const SurferFan = ({
    roomId: propRoomId,
    playerSide: propPlayerSide,
    side: propSide,
    matchState: propMatchState,
    config: propConfig,
    winner: propWinner
}) => {
    // =========================================================================
    // 1. DETECCIÓN MULTI-TENANT DE SALA
    // =========================================================================
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = useMemo(() => {
        const rawId = (
            propRoomId ||
            searchParams.get('roomId') ||
            searchParams.get('r') ||
            propMatchState?.roomId ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
        return String(rawId || '').trim();
    }, [propRoomId, searchParams, propMatchState]);

    // =========================================================================
    // 2. RECUPERACIÓN DE SESIÓN PERSISTENTE
    // =========================================================================
    const [selectedSide, setSelectedSide] = useState(() => {
        const directProp = propPlayerSide || propSide || searchParams.get('side') || searchParams.get('playerSide');
        if (directProp) {
            const norm = String(directProp).toLowerCase();
            return (norm === 'player1' || norm === 'j1' || norm === '1' || norm === 'left') ? 'player1' : 'player2';
        }

        if (typeof sessionStorage !== 'undefined') {
            const savedRoomSide = activeRoomId ? sessionStorage.getItem(`surfer_player_${activeRoomId}`) : null;
            if (savedRoomSide) return savedRoomSide;
            const savedSide = sessionStorage.getItem(STORAGE_KEY_SIDE);
            if (savedSide) return savedSide;
        }
        return '';
    });

    const [playerName, setPlayerName] = useState(() => {
        if (typeof sessionStorage !== 'undefined') {
            return sessionStorage.getItem(STORAGE_KEY_NAME) || '';
        }
        return '';
    });

    // =========================================================================
    // 3. ESTADOS DE RED Y TIEMPO REAL
    // =========================================================================
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [isKickedOut, setIsKickedOut] = useState(false);
    const [kickedReason, setKickedReason] = useState('');

    // Estado del juego
    const [phase, setPhase] = useState(() => {
        const raw = propMatchState?.phase || propMatchState?.state || 'idle';
        return String(raw).toLowerCase();
    });
    const [countdownValue, setCountdownValue] = useState(3);
    const [winnerResult, setWinnerResult] = useState(propWinner || null);
    const [winnerReason, setWinnerReason] = useState('');
    const [remainingSeconds, setRemainingSeconds] = useState(45);

    // Datos de telemetría de jugadores
    const [player1Data, setPlayer1Data] = useState({
        side: 'player1',
        name: 'Jugador 1',
        avatarUrl: '',
        score: 0,
        hits: 0,
        coins: 0,
        distance: 0,
        currentLane: 1,
        isConnected: false
    });

    const [player2Data, setPlayer2Data] = useState({
        side: 'player2',
        name: 'Jugador 2',
        avatarUrl: '',
        score: 0,
        hits: 0,
        coins: 0,
        distance: 0,
        currentLane: 1,
        isConnected: false
    });

    // Configuración y Tema
    const [configData, setConfigData] = useState(() => ({
        laneCount: 3,
        canJump: true,
        canSlide: true,
        timeLimitSeconds: 45,
        ...(propConfig || {})
    }));

    const [themeData, setThemeData] = useState(() => {
        return propMatchState?.theme || null;
    });

    // Feedback táctil y visual
    const [lastAction, setLastAction] = useState({ action: '', timestamp: 0 });
    const [isHitShaking, setIsHitShaking] = useState(false);
    const [touchTrail, setTouchTrail] = useState([]);
    const [soundEnabled, setSoundEnabled] = useState(true);

    // Referencias
    const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
    const touchCurrentRef = useRef({ x: 0, y: 0 });
    const isSwipingRef = useRef(false);
    const wakeLockRef = useRef(null);
    const shakeTimeoutRef = useRef(null);

    // =========================================================================
    // 4. SCREEN WAKE LOCK API
    // =========================================================================
    const requestWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator && !wakeLockRef.current) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            }
        } catch (err) {
            console.warn('[SurferFan] Screen Wake Lock not available or rejected:', err);
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        try {
            if (wakeLockRef.current) {
                await wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        } catch (err) {
            console.warn('[SurferFan] Screen Wake Lock release error:', err);
        }
    }, []);

    useEffect(() => {
        requestWakeLock();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLock();
        };
    }, [requestWakeLock, releaseWakeLock]);

    // =========================================================================
    // 5. CARGA INICIAL DE CONFIGURACIÓN Y ESTADO DE SALA
    // =========================================================================
    useEffect(() => {
        let isMounted = true;
        if (!activeRoomId) return;

        const loadInitialRoomData = async () => {
            try {
                const configRes = await GetConfig(activeRoomId);
                if (isMounted && configRes?.success && configRes?.data) {
                    setConfigData((prev) => ({ ...prev, ...configRes.data }));
                }

                const stateRes = await GetRealtimeState(activeRoomId);
                if (isMounted && stateRes?.success && stateRes?.data) {
                    const data = stateRes.data;
                    if (data.phase) setPhase(String(data.phase).toLowerCase());
                    if (data.theme) setThemeData(data.theme);
                    if (data.player1) setPlayer1Data((prev) => ({ ...prev, ...data.player1 }));
                    if (data.player2) setPlayer2Data((prev) => ({ ...prev, ...data.player2 }));
                    if (data.winner) setWinnerResult(data.winner);
                }
            } catch (err) {
                console.warn('[SurferFan] Error al cargar configuración inicial:', err);
            }
        };

        loadInitialRoomData();

        return () => {
            isMounted = false;
        };
    }, [activeRoomId]);

    // =========================================================================
    // 6. MANEJO DE SELECCIÓN DE PERSONAJE / BANDO
    // =========================================================================
    const handleSelectSide = useCallback(async (sideToSelect) => {
        if (!sideToSelect) return;
        audioSynth.unlock();
        if (soundEnabled) audioSynth.playSwipe();

        if (navigator.vibrate) {
            navigator.vibrate(50);
        }

        const normalizedSide = (sideToSelect === 'player1' || sideToSelect === 'j1' || sideToSelect === '1') ? 'player1' : 'player2';
        const chosenName = playerName || (normalizedSide === 'player1' ? (themeData?.player1Name || 'Jugador 1') : (themeData?.player2Name || 'Jugador 2'));

        setSelectedSide(normalizedSide);
        setIsKickedOut(false);
        setKickedReason('');

        // Persistir en sessionStorage
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(STORAGE_KEY_SIDE, normalizedSide);
                sessionStorage.setItem(STORAGE_KEY_NAME, chosenName);
                if (activeRoomId) {
                    sessionStorage.setItem(`surfer_player_${activeRoomId}`, normalizedSide);
                }
            }
        } catch (e) {
            console.warn('[SurferFan] Storage error:', e);
        }

        // Registrar lado en el backend / Hub
        try {
            const connId = SignalRService.getConnectionId() || '';
            if (activeRoomId) {
                // Intentar por Hub SignalR primero
                SignalRService.invoke('Surfer_SelectPlayerSide', activeRoomId, normalizedSide, chosenName)
                    .catch(() => {
                        // Fallback REST
                        SelectPlayerSide(activeRoomId, connId, normalizedSide, chosenName);
                    });
            }
        } catch (err) {
            console.warn('[SurferFan] Error registrando selección de personaje:', err);
        }
    }, [activeRoomId, playerName, soundEnabled, themeData]);

    const handleLeaveSide = useCallback(() => {
        setSelectedSide('');
        setIsKickedOut(false);
        setKickedReason('');
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.removeItem(STORAGE_KEY_SIDE);
                if (activeRoomId) {
                    sessionStorage.removeItem(`surfer_player_${activeRoomId}`);
                }
            }
        } catch (e) {}
    }, [activeRoomId]);

    // =========================================================================
    // 7. WEBSOCKET / SIGNALR EN TIEMPO REAL
    // =========================================================================
    useEffect(() => {
        if (!activeRoomId) return;

        const handleIncomingMessage = (envelope) => {
            if (!envelope) return;

            const type = String(envelope.type || envelope.Type || '').toLowerCase();
            const payload = envelope.payload !== undefined ? envelope.payload : envelope.data;

            switch (type) {
                case 'surfer_countdown_tick': {
                    const count = payload?.countdownValue ?? payload?.value ?? payload?.count ?? 3;
                    setPhase('countdown');
                    setCountdownValue(count);
                    if (soundEnabled) {
                        if (count > 0) audioSynth.playCountdownTick();
                        else audioSynth.playCountdownGo();
                    }
                    if (navigator.vibrate) navigator.vibrate(count === 0 ? [50, 50, 100] : 40);
                    break;
                }

                case 'surfer_match_started': {
                    setPhase('running');
                    setCountdownValue(0);
                    if (soundEnabled) audioSynth.playCountdownGo();
                    if (navigator.vibrate) navigator.vibrate([40, 40, 80]);
                    break;
                }

                case 'surfer_match_paused': {
                    setPhase('paused');
                    break;
                }

                case 'surfer_match_resumed': {
                    setPhase('running');
                    break;
                }

                case 'surfer_match_finished': {
                    setPhase('finished');
                    const w = payload?.winner || payload?.Winner || null;
                    const r = payload?.winnerReason || payload?.WinnerReason || '';
                    setWinnerResult(w);
                    setWinnerReason(r);
                    if (payload?.player1) setPlayer1Data((prev) => ({ ...prev, ...payload.player1 }));
                    if (payload?.player2) setPlayer2Data((prev) => ({ ...prev, ...payload.player2 }));
                    if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
                    break;
                }

                case 'surfer_match_reset': {
                    setPhase('game_ready');
                    setWinnerResult(null);
                    setWinnerReason('');
                    setCountdownValue(3);
                    setPlayer1Data((prev) => ({ ...prev, score: 0, hits: 0, coins: 0, distance: 0, currentLane: 1 }));
                    setPlayer2Data((prev) => ({ ...prev, score: 0, hits: 0, coins: 0, distance: 0, currentLane: 1 }));
                    break;
                }

                case 'surfer_player_hit': {
                    const hitSide = payload?.side || payload?.Side;
                    if (hitSide === 'player1') {
                        setPlayer1Data((prev) => ({ ...prev, hits: (prev.hits || 0) + 1, score: payload?.score ?? prev.score }));
                    } else if (hitSide === 'player2') {
                        setPlayer2Data((prev) => ({ ...prev, hits: (prev.hits || 0) + 1, score: payload?.score ?? prev.score }));
                    }

                    // Si el impacto corresponde al corredor del usuario
                    if (!hitSide || hitSide === selectedSide) {
                        if (soundEnabled) audioSynth.playHit();
                        if (navigator.vibrate) navigator.vibrate([80, 50, 80]);

                        setIsHitShaking(true);
                        if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
                        shakeTimeoutRef.current = setTimeout(() => {
                            setIsHitShaking(false);
                        }, 400);
                    }
                    break;
                }

                case 'surfer_score_updated': {
                    const side = payload?.side || payload?.Side;
                    if (side === 'player1') {
                        setPlayer1Data((prev) => ({ ...prev, ...payload }));
                    } else if (side === 'player2') {
                        setPlayer2Data((prev) => ({ ...prev, ...payload }));
                    }
                    if (payload?.reason === 'coin' || payload?.reason === 'bonus') {
                        if (side === selectedSide) {
                            if (soundEnabled) audioSynth.playCoin();
                            if (navigator.vibrate) navigator.vibrate(35);
                        }
                    }
                    break;
                }

                case 'surfer_player_action_received': {
                    const actSide = payload?.side || payload?.Side;
                    const lane = payload?.currentLane;
                    if (actSide === 'player1' && lane !== undefined) {
                        setPlayer1Data((prev) => ({ ...prev, currentLane: lane }));
                    } else if (actSide === 'player2' && lane !== undefined) {
                        setPlayer2Data((prev) => ({ ...prev, currentLane: lane }));
                    }
                    break;
                }

                case 'surfer_game_scene_shown': {
                    setPhase('game_ready');
                    if (payload?.theme) setThemeData(payload.theme);
                    break;
                }

                case 'surfer_cover_shown': {
                    setPhase('cover');
                    if (payload?.theme) setThemeData(payload.theme);
                    break;
                }

                case 'surfer_player_joined': {
                    const joinedSide = payload?.side || payload?.Side;
                    if (joinedSide === 'player1') {
                        setPlayer1Data((prev) => ({ ...prev, isConnected: true, name: payload?.name || prev.name }));
                    } else if (joinedSide === 'player2') {
                        setPlayer2Data((prev) => ({ ...prev, isConnected: true, name: payload?.name || prev.name }));
                    }
                    break;
                }

                case 'surfer_player_left': {
                    const leftSide = payload?.side || payload?.Side;
                    if (leftSide === 'player1') {
                        setPlayer1Data((prev) => ({ ...prev, isConnected: false }));
                    } else if (leftSide === 'player2') {
                        setPlayer2Data((prev) => ({ ...prev, isConnected: false }));
                    }
                    break;
                }

                default:
                    break;
            }
        };

        const handleStatusChange = (status) => {
            setConnectionStatus(status);
        };

        const handleKickedOut = (envelope) => {
            console.warn('[SurferFan] Kicked out handler triggered:', envelope);
            setIsKickedOut(true);
            setKickedReason(envelope?.message || 'Otro aficionado ha tomado el control de este personaje en la pantalla gigante.');
            setSelectedSide('');
            if (navigator.vibrate) {
                navigator.vibrate([150, 80, 150]);
            }
        };

        ConnectWebSocketForSurferFan(
            handleIncomingMessage,
            handleStatusChange,
            activeRoomId,
            selectedSide,
            handleKickedOut
        );

        return () => {
            DisconnectWebSocketForSurferFan();
            if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
        };
    }, [activeRoomId, selectedSide, soundEnabled]);

    // =========================================================================
    // 8. ENVÍO DE ACCIONES GESTUALES (SWIPES)
    // =========================================================================
    const dispatchSwipeAction = useCallback((action) => {
        if (!selectedSide || !activeRoomId) return;

        // Feedback háptico y auditivo
        if (navigator.vibrate) {
            navigator.vibrate(40);
        }
        if (soundEnabled) {
            audioSynth.playSwipe();
        }

        // Estado visual del swipe
        setLastAction({ action, timestamp: Date.now() });

        // Envío autoritativo a través de Hub SignalR y fallback REST
        try {
            SignalRService.invoke('Surfer_SendPlayerAction', activeRoomId, selectedSide, action)
                .catch(() => {
                    const connId = SignalRService.getConnectionId() || '';
                    SendPlayerAction(activeRoomId, connId, selectedSide, action);
                });
        } catch (err) {
            console.warn('[SurferFan] Error al emitir acción:', err);
        }
    }, [activeRoomId, selectedSide, soundEnabled]);

    // =========================================================================
    // 9. GESTIÓN DE EVENTOS TÁCTILES (100% TOUCHPAD GESTUAL)
    // =========================================================================
    const handleTouchStart = (e) => {
        audioSynth.unlock();
        if (e.touches && e.touches.length > 0) {
            const touch = e.touches[0];
            touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now()
            };
            touchCurrentRef.current = {
                x: touch.clientX,
                y: touch.clientY
            };
            isSwipingRef.current = true;

            // Registrar punto de inicio para la estela táctil
            setTouchTrail([
                { x: touch.clientX, y: touch.clientY, id: Date.now() }
            ]);
        }
    };

    const handleTouchMove = (e) => {
        if (!isSwipingRef.current) return;
        if (e.touches && e.touches.length > 0) {
            const touch = e.touches[0];
            touchCurrentRef.current = {
                x: touch.clientX,
                y: touch.clientY
            };

            // Mantener estela de los últimos 5 puntos
            setTouchTrail((prev) => [
                ...prev.slice(-4),
                { x: touch.clientX, y: touch.clientY, id: Date.now() }
            ]);
        }
    };

    const handleTouchEnd = (e) => {
        if (!isSwipingRef.current) return;
        isSwipingRef.current = false;

        const startX = touchStartRef.current.x;
        const startY = touchStartRef.current.y;
        const endX = touchCurrentRef.current.x;
        const endY = touchCurrentRef.current.y;

        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        // Umbral mínimo para reconocer un swipe
        const SWIPE_THRESHOLD = 30;

        if (absX >= SWIPE_THRESHOLD || absY >= SWIPE_THRESHOLD) {
            if (absX > absY) {
                // Swipe Horizontal (Izquierda / Derecha)
                if (deltaX < 0) {
                    dispatchSwipeAction('left');
                } else {
                    dispatchSwipeAction('right');
                }
            } else {
                // Swipe Vertical (Salto / Slide)
                if (deltaY < 0) {
                    if (configData.canJump !== false) {
                        dispatchSwipeAction('jump');
                    }
                } else {
                    if (configData.canSlide !== false) {
                        dispatchSwipeAction('slide');
                    }
                }
            }
        }

        // Limpiar estela después de un breve delay
        setTimeout(() => {
            setTouchTrail([]);
        }, 200);
    };

    const handleTouchCancel = () => {
        isSwipingRef.current = false;
        setTouchTrail([]);
    };

    // =========================================================================
    // 10. PROPIEDADES DERIVADAS DEL JUGADOR ACTUAL
    // =========================================================================
    const myData = useMemo(() => {
        return selectedSide === 'player1' ? player1Data : player2Data;
    }, [selectedSide, player1Data, player2Data]);

    const opponentData = useMemo(() => {
        return selectedSide === 'player1' ? player2Data : player1Data;
    }, [selectedSide, player1Data, player2Data]);

    const myColor = useMemo(() => {
        if (selectedSide === 'player1') {
            return themeData?.primaryColor || DEFAULT_COLORS.player1;
        }
        return themeData?.secondaryColor || DEFAULT_COLORS.player2;
    }, [selectedSide, themeData]);

    const myAvatarUrl = useMemo(() => {
        if (selectedSide === 'player1') {
            return ResolveMediaUrl(themeData?.player1Url) || DEFAULT_AVATAR_P1;
        }
        return ResolveMediaUrl(themeData?.player2Url) || DEFAULT_AVATAR_P2;
    }, [selectedSide, themeData]);

    const opponentAvatarUrl = useMemo(() => {
        if (selectedSide === 'player1') {
            return ResolveMediaUrl(themeData?.player2Url) || DEFAULT_AVATAR_P2;
        }
        return ResolveMediaUrl(themeData?.player1Url) || DEFAULT_AVATAR_P1;
    }, [selectedSide, themeData]);

    // Evaluación de podio y victoria
    const isWinner = useMemo(() => {
        if (!winnerResult) return false;
        const normW = String(winnerResult).toLowerCase();
        return (normW === selectedSide || (selectedSide === 'player1' && (normW === 'j1' || normW === 'p1' || normW === '1')) || (selectedSide === 'player2' && (normW === 'j2' || normW === 'p2' || normW === '2')));
    }, [winnerResult, selectedSide]);

    const isDraw = useMemo(() => {
        if (!winnerResult) return false;
        const normW = String(winnerResult).toLowerCase();
        return normW === 'draw' || normW === 'tie' || normW === 'empate';
    }, [winnerResult]);

    // =========================================================================
    // 11. RENDERIZADO CONDICIONAL DE VISTAS
    // =========================================================================

    // VISTA 5: MODAL DE DESALOJO PACÍFICO (PATRÓN TACOS)
    if (isKickedOut) {
        return (
            <div className="surfer-fan surfer-fan--kicked">
                <div className="surfer-fan__kicked-card">
                    <div className="surfer-fan__kicked-icon">
                        <i className="bi bi-exclamation-octagon-fill"></i>
                    </div>
                    <h2 className="surfer-fan__kicked-title">Sesión Reasignada</h2>
                    <p className="surfer-fan__kicked-text">
                        {kickedReason || 'Otro aficionado ha tomado el control de este personaje en la pantalla gigante.'}
                    </p>
                    <button
                        type="button"
                        className="surfer-fan__btn-primary"
                        onClick={() => {
                            setIsKickedOut(false);
                            setSelectedSide('');
                        }}
                    >
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Elegir Otro Personaje
                    </button>
                </div>
            </div>
        );
    }

    // VISTA 1: LOBBY DE SELECCIÓN DE PERSONAJE
    if (!selectedSide) {
        const p1Avatar = ResolveMediaUrl(themeData?.player1Url) || DEFAULT_AVATAR_P1;
        const p2Avatar = ResolveMediaUrl(themeData?.player2Url) || DEFAULT_AVATAR_P2;
        const p1Name = themeData?.player1Name || 'Corredor 1';
        const p2Name = themeData?.player2Name || 'Corredor 2';

        return (
            <div className="surfer-fan surfer-fan--lobby">
                {/* Cabecera del Lobby */}
                <header className="surfer-fan__lobby-header">
                    <div className="surfer-fan__game-badge">
                        <i className="bi bi-controller me-1"></i>
                        ESQUIVADOR 1 VS 1
                    </div>
                    <h1 className="surfer-fan__lobby-title">Selecciona tu personaje</h1>
                    <p className="surfer-fan__lobby-subtitle">
                        Toca un corredor para conectar tu celular con la pantalla gigante
                    </p>
                </header>

                {/* Input opcional de nombre */}
                <div className="surfer-fan__name-field">
                    <label className="surfer-fan__name-label">
                        <i className="bi bi-person-fill me-1"></i> Tu Nombre / Apodo (Opcional):
                    </label>
                    <input
                        type="text"
                        className="surfer-fan__name-input"
                        placeholder="Escribe tu apodo..."
                        maxLength={18}
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                    />
                </div>

                {/* Tiles de Selección de Personaje */}
                <div className="surfer-fan__tiles-container">
                    {/* TILE JUGADOR 1 (CIAN) */}
                    <div
                        className="surfer-fan__tile surfer-fan__tile--p1"
                        onClick={() => handleSelectSide('player1')}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="surfer-fan__tile-glow"></div>
                        <div className="surfer-fan__tile-avatar-wrap">
                            <img
                                src={p1Avatar}
                                alt={p1Name}
                                className="surfer-fan__tile-avatar"
                                onError={(e) => { e.target.src = DEFAULT_AVATAR_P1; }}
                            />
                            <span className="surfer-fan__tile-tag">JUGADOR 1</span>
                        </div>
                        <h3 className="surfer-fan__tile-name">{p1Name}</h3>
                        <span className="surfer-fan__tile-action">
                            <i className="bi bi-hand-index-thumb-fill me-1"></i>
                            TOCAR PARA JUGAR
                        </span>
                    </div>

                    {/* TILE JUGADOR 2 (ROSA NEÓN) */}
                    <div
                        className="surfer-fan__tile surfer-fan__tile--p2"
                        onClick={() => handleSelectSide('player2')}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="surfer-fan__tile-glow"></div>
                        <div className="surfer-fan__tile-avatar-wrap">
                            <img
                                src={p2Avatar}
                                alt={p2Name}
                                className="surfer-fan__tile-avatar"
                                onError={(e) => { e.target.src = DEFAULT_AVATAR_P2; }}
                            />
                            <span className="surfer-fan__tile-tag">JUGADOR 2</span>
                        </div>
                        <h3 className="surfer-fan__tile-name">{p2Name}</h3>
                        <span className="surfer-fan__tile-action">
                            <i className="bi bi-hand-index-thumb-fill me-1"></i>
                            TOCAR PARA JUGAR
                        </span>
                    </div>
                </div>

                {/* Footer del Lobby */}
                <footer className="surfer-fan__lobby-footer">
                    <div className="surfer-fan__status-pill">
                        <span className={`surfer-fan__dot surfer-fan__dot--${connectionStatus === 'connected' ? 'online' : 'connecting'}`}></span>
                        {connectionStatus === 'connected' ? 'Conectado a la Sala' : 'Sincronizando con el Estadio...'}
                    </div>
                </footer>
            </div>
        );
    }

    // VISTA 4: PANTALLA DE PODIO / FIN DE CARRERA
    if (phase === 'finished') {
        return (
            <div className="surfer-fan surfer-fan--finished">
                <div className="surfer-fan__podium-card">
                    {isWinner ? (
                        <div className="surfer-fan__podium-header surfer-fan__podium-header--win">
                            <div className="surfer-fan__podium-trophy">🏆</div>
                            <h2 className="surfer-fan__podium-title">¡VICTORIA!</h2>
                            <p className="surfer-fan__podium-subtitle">¡Has conquistado la pista!</p>
                        </div>
                    ) : isDraw ? (
                        <div className="surfer-fan__podium-header surfer-fan__podium-header--draw">
                            <div className="surfer-fan__podium-trophy">🤝</div>
                            <h2 className="surfer-fan__podium-title">¡EMPATE!</h2>
                            <p className="surfer-fan__podium-subtitle">¡Una carrera sumamente reñida!</p>
                        </div>
                    ) : (
                        <div className="surfer-fan__podium-header surfer-fan__podium-header--loss">
                            <div className="surfer-fan__podium-trophy">🥈</div>
                            <h2 className="surfer-fan__podium-title">FIN DE CARRERA</h2>
                            <p className="surfer-fan__podium-subtitle">¡Gran desempeño en la pista!</p>
                        </div>
                    )}

                    {/* Avatar y Datos del Jugador */}
                    <div className="surfer-fan__podium-player">
                        <img
                            src={myAvatarUrl}
                            alt="Mi Corredor"
                            className="surfer-fan__podium-avatar"
                            style={{ borderColor: myColor }}
                            onError={(e) => { e.target.src = selectedSide === 'player1' ? DEFAULT_AVATAR_P1 : DEFAULT_AVATAR_P2; }}
                        />
                        <span className="surfer-fan__podium-player-name">{myData.name || (selectedSide === 'player1' ? 'Jugador 1' : 'Jugador 2')}</span>
                    </div>

                    {/* Resumen de Estadísticas */}
                    <div className="surfer-fan__stats-grid">
                        <div className="surfer-fan__stat-box">
                            <span className="surfer-fan__stat-label">Puntaje Final</span>
                            <span className="surfer-fan__stat-value surfer-fan__stat-value--points">{myData.score || 0} pts</span>
                        </div>
                        <div className="surfer-fan__stat-box">
                            <span className="surfer-fan__stat-label">Monedas</span>
                            <span className="surfer-fan__stat-value surfer-fan__stat-value--coins">🪙 {myData.coins || 0}</span>
                        </div>
                        <div className="surfer-fan__stat-box">
                            <span className="surfer-fan__stat-label">Choques</span>
                            <span className="surfer-fan__stat-value surfer-fan__stat-value--hits">💥 {myData.hits || 0}</span>
                        </div>
                        <div className="surfer-fan__stat-box">
                            <span className="surfer-fan__stat-label">Distancia</span>
                            <span className="surfer-fan__stat-value surfer-fan__stat-value--dist">{Math.round(myData.distance || 0)} m</span>
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="surfer-fan__podium-actions">
                        <button
                            type="button"
                            className="surfer-fan__btn-primary"
                            onClick={handleLeaveSide}
                        >
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Volver a Elegir Personaje
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // VISTA 2: SALA DE ESPERA / CONTEO REGRESIVO
    if (phase === 'idle' || phase === 'cover' || phase === 'game_ready' || phase === 'countdown') {
        return (
            <div className="surfer-fan surfer-fan--waiting">
                {/* Conteo Regresivo Overlay */}
                {phase === 'countdown' ? (
                    <div className="surfer-fan__countdown-overlay">
                        <div className="surfer-fan__countdown-number" key={countdownValue}>
                            {countdownValue === 0 ? '¡GO!' : countdownValue}
                        </div>
                        <div className="surfer-fan__countdown-label">¡PREPÁRATE PARA DESLIZAR!</div>
                    </div>
                ) : (
                    <div className="surfer-fan__waiting-content">
                        {/* Insignia de Conexión */}
                        <div className="surfer-fan__connected-badge">
                            <span className="surfer-fan__dot surfer-fan__dot--online"></span>
                            Conectado a la Pantalla Gigante
                        </div>

                        {/* Corredor Seleccionado */}
                        <div className="surfer-fan__selected-card" style={{ borderColor: myColor }}>
                            <div className="surfer-fan__selected-avatar-wrap">
                                <img
                                    src={myAvatarUrl}
                                    alt="Corredor"
                                    className="surfer-fan__selected-avatar"
                                    onError={(e) => { e.target.src = selectedSide === 'player1' ? DEFAULT_AVATAR_P1 : DEFAULT_AVATAR_P2; }}
                                />
                            </div>
                            <span className="surfer-fan__selected-side-tag" style={{ backgroundColor: myColor }}>
                                {selectedSide === 'player1' ? 'JUGADOR 1' : 'JUGADOR 2'}
                            </span>
                            <h2 className="surfer-fan__selected-title">
                                {myData.name || (selectedSide === 'player1' ? 'Jugador 1' : 'Jugador 2')}
                            </h2>
                            <p className="surfer-fan__selected-hint">
                                Esperando que el operador inicie la carrera...
                            </p>
                        </div>

                        {/* Guía Rápida de Gestos */}
                        <div className="surfer-fan__guide-card">
                            <h4 className="surfer-fan__guide-title">
                                <i className="bi bi-phone-vibrate me-2"></i>
                                Control 100% Gestual por Swipe
                            </h4>
                            <div className="surfer-fan__guide-grid">
                                <div className="surfer-fan__guide-item">
                                    <div className="surfer-fan__guide-icon">↔️</div>
                                    <span className="surfer-fan__guide-text">Desliza Izq / Der para cambiar carril</span>
                                </div>
                                {configData.canJump !== false && (
                                    <div className="surfer-fan__guide-item">
                                        <div className="surfer-fan__guide-icon">⬆️</div>
                                        <span className="surfer-fan__guide-text">Desliza Arriba para saltar</span>
                                    </div>
                                )}
                                {configData.canSlide !== false && (
                                    <div className="surfer-fan__guide-item">
                                        <div className="surfer-fan__guide-icon">⬇️</div>
                                        <span className="surfer-fan__guide-text">Desliza Abajo para agacharte</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Botón para cambiar de corredor */}
                        <button
                            type="button"
                            className="surfer-fan__btn-secondary"
                            onClick={handleLeaveSide}
                        >
                            <i className="bi bi-arrow-left-circle me-2"></i>
                            Cambiar de Personaje
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // VISTA 3: MANDO DE JUEGO 100% GESTUAL POR SWIPE (FASE RUNNING / PLAYING / PAUSED)
    return (
        <div
            className={`surfer-fan surfer-fan--playing ${isHitShaking ? 'surfer-fan--shake' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* HUD SUPERIOR FLOTANTE */}
            <header className="surfer-fan__hud">
                {/* Perfil Jugador */}
                <div className="surfer-fan__hud-player" style={{ borderColor: myColor }}>
                    <img
                        src={myAvatarUrl}
                        alt="Mi Jugador"
                        className="surfer-fan__hud-avatar"
                        onError={(e) => { e.target.src = selectedSide === 'player1' ? DEFAULT_AVATAR_P1 : DEFAULT_AVATAR_P2; }}
                    />
                    <div className="surfer-fan__hud-info">
                        <span className="surfer-fan__hud-side" style={{ color: myColor }}>
                            {selectedSide === 'player1' ? 'J1' : 'J2'}
                        </span>
                        <span className="surfer-fan__hud-name">
                            {myData.name || (selectedSide === 'player1' ? 'Jugador 1' : 'Jugador 2')}
                        </span>
                    </div>
                </div>

                {/* Puntuación y Monedas */}
                <div className="surfer-fan__hud-stats">
                    <div className="surfer-fan__hud-stat-item surfer-fan__hud-stat-item--score">
                        <span className="surfer-fan__hud-stat-label">PUNTOS</span>
                        <span className="surfer-fan__hud-stat-num">{myData.score || 0}</span>
                    </div>
                    <div className="surfer-fan__hud-stat-item">
                        <span className="surfer-fan__hud-stat-icon">🪙</span>
                        <span className="surfer-fan__hud-stat-num">{myData.coins || 0}</span>
                    </div>
                    <div className="surfer-fan__hud-stat-item surfer-fan__hud-stat-item--hits">
                        <span className="surfer-fan__hud-stat-icon">💥</span>
                        <span className="surfer-fan__hud-stat-num">{myData.hits || 0}</span>
                    </div>
                </div>
            </header>

            {/* ZONA CENTRAL TOUCHPAD 100% GESTUAL */}
            <main className="surfer-fan__touchpad">
                {/* Cuadrícula de fondo Cyberpunk */}
                <div className="surfer-fan__touchpad-grid"></div>

                {/* Flechas indicadoras sutiles */}
                <div className="surfer-fan__touchpad-hints">
                    {configData.canJump !== false && (
                        <div className={`surfer-fan__hint-arrow surfer-fan__hint-arrow--up ${lastAction.action === 'jump' ? 'surfer-fan__hint-arrow--active' : ''}`}>
                            <i className="bi bi-chevron-up"></i>
                            <span>SALTAR</span>
                        </div>
                    )}
                    <div className="surfer-fan__hint-horizontal">
                        <div className={`surfer-fan__hint-arrow surfer-fan__hint-arrow--left ${lastAction.action === 'left' ? 'surfer-fan__hint-arrow--active' : ''}`}>
                            <i className="bi bi-chevron-left"></i>
                            <span>IZQ</span>
                        </div>
                        <div className="surfer-fan__touchpad-center-badge">
                            <i className="bi bi-hand-index-thumb"></i>
                            <span>DESLIZA AQUÍ</span>
                        </div>
                        <div className={`surfer-fan__hint-arrow surfer-fan__hint-arrow--right ${lastAction.action === 'right' ? 'surfer-fan__hint-arrow--active' : ''}`}>
                            <i className="bi bi-chevron-right"></i>
                            <span>DER</span>
                        </div>
                    </div>
                    {configData.canSlide !== false && (
                        <div className={`surfer-fan__hint-arrow surfer-fan__hint-arrow--down ${lastAction.action === 'slide' ? 'surfer-fan__hint-arrow--active' : ''}`}>
                            <i className="bi bi-chevron-down"></i>
                            <span>AGANCHARSE</span>
                        </div>
                    )}
                </div>

                {/* Feedback de Último Swipe Registrado */}
                {lastAction.action && (Date.now() - lastAction.timestamp < 350) && (
                    <div className={`surfer-fan__swipe-feedback surfer-fan__swipe-feedback--${lastAction.action}`} key={lastAction.timestamp}>
                        {lastAction.action === 'left' && <i className="bi bi-arrow-left"></i>}
                        {lastAction.action === 'right' && <i className="bi bi-arrow-right"></i>}
                        {lastAction.action === 'jump' && <i className="bi bi-arrow-up"></i>}
                        {lastAction.action === 'slide' && <i className="bi bi-arrow-down"></i>}
                    </div>
                )}

                {/* Estela Táctil (Trail Glow) */}
                {touchTrail.map((pt, idx) => (
                    <div
                        key={pt.id}
                        className="surfer-fan__trail-point"
                        style={{
                            left: `${pt.x}px`,
                            top: `${pt.y}px`,
                            opacity: (idx + 1) / touchTrail.length,
                            backgroundColor: myColor,
                            boxShadow: `0 0 16px ${myColor}`
                        }}
                    ></div>
                ))}
            </main>

            {/* PAUSA OVERLAY */}
            {phase === 'paused' && (
                <div className="surfer-fan__pause-overlay">
                    <div className="surfer-fan__pause-card">
                        <i className="bi bi-pause-circle-fill surfer-fan__pause-icon"></i>
                        <h3 className="surfer-fan__pause-title">Carrera Pausada</h3>
                        <p className="surfer-fan__pause-text">El operador ha detenido la dinámica temporalmente</p>
                    </div>
                </div>
            )}

            {/* FOOTER INFORMATIVO */}
            <footer className="surfer-fan__hud-footer">
                <div className="surfer-fan__hud-status">
                    <span className="surfer-fan__dot surfer-fan__dot--online"></span>
                    <span>MANDO ACTIVO</span>
                </div>
                <button
                    type="button"
                    className="surfer-fan__sound-toggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSoundEnabled(!soundEnabled);
                    }}
                >
                    <i className={`bi bi-volume-${soundEnabled ? 'up' : 'mute'}-fill`}></i>
                </button>
            </footer>
        </div>
    );
};

export default SurferFan;
